/* eslint-disable max-lines */
/// <reference types="google-publisher-tag" />

export enum PerformanceMarkName {
  AE_FIRST_LINE_EXEC = "ae.firstLineExec",
  AE_AFTER_INIT_PLUGINS = "ae.afterInitPlugins",
  AE_AFTER_GPT_WRAPPER_INIT = "ae.afterGptWrapperInit",
  AE_AD_ENGINE_INIT = "ae.adEngineInit",
  AE_ON_BIDDERS_INIT = "ae.onBiddersInit",
  AE_CONFIG_CALLBACK_STARTED = "ae.beforeAuction.configCallbackStarted",
  AE_CONFIG_CALLBACK_COMPLETE = "ae.beforeAuction.configCallbackComplete",
  AE_AUCTION_START = "ae.auctionStart",
  AE_AUCTION_START_A9_START = "ae.auctionStart.a9.bidStart",
  AE_AUCTION_END = "ae.auctionEnd",
  AE_BEFORE_FIRST_CALL_ADS_REQUEST = "ae.beforeFirstCallAdsRequest",
  AE_GPT_SLOT_REQUESTED_INITIAL = "ae.gpt.slotRequested.initial",
  AE_GPT_SLOT_RESPONSE_RECEIVED_INITIAL = "ae.gpt.slotResponseReceived.initial",
  AE_GPT_SLOT_RENDERED_ENDED_INITIAL = "ae.gpt.slotRenderEnded.initial",
  AE_GPT_SLOT_SLOT_ONLOAD_INITIAL = "ae.gpt.slotOnload.initial",
}

/**
 * Keeps track of known durations (performance.measure) that we are tracking
 *
 * NOTE: when these durations are sent as metrics, they show up with the string
 * name co-mingled with PerformanceMarkName. This means some metrics measure the time
 * from browser load (time zero), while other metrics measure time from a different time
 * origin.
 *
 * To help differentiate these, all of the measures in this section should start with
 * the prefix `ae.d` to designate they are durations measured from a different time origin.
 */
export enum PerformanceMeasureName {
  AE_AUCTION_DURATION_A9_BID_TOTAL_TIME = "ae.d.auctionDuration.a9.bidTotalTime",
  AE_AUCTION_DURATION = "ae.d.auctionDuration",
  AE_DURATION_TO_BID = "ae.d.firstLineExec_to_auctionStart",
  AE_CONFIG_CALLBACK_DURATION = "ae.d.configCallbackDuration",
}

/**
 * Metadata to be included in the performance.mark markOptions.detail attribute
 *
 * This is a subset of the AdEngine
 */
export type PerformanceMarkRefreshMetadata = {
  /**
   * Unique identifier for the auction. This is passed to all the bidders and can be used to
   * associated tracking across different bidders and events. This is a uuid string.
   *
   * Example: `f3b8c1e0-4d2a-4f5b-8c7e-9d6f1a2e3b4c`
   */
  auctionId: string;
  /**
   * The render cycle count for the AdEngine. The first render cycle is 1 and increments by 1.
   * This can be used for tracking, debugging, and differentiating the first render from subsequent
   * renders.
   */
  renderCycleCount: number;
};

/** *****************************************************************************
 *
 * TrackEvent
 *
 *******************************************************************************/

export enum BidderEvent {
  BIDDER_RESPONSE = "bid_response",
}

export enum InfoEvent {
  SLOT_REQUESTED_INFO = "requested_info",
  SLOT_CODE_SERVED_INFO = "code_served_info",
  SLOT_RENDERED_INFO = "success_info",
  SLOT_COLLAPSE_INFO = "collapse",
  SLOT_FORCE_CLOSE_INFO = "force-close",
  SLOT_CLICKED = "clicked",
}

export enum IdentityInfoEvent {
  IDENTITY_INFO = "identity_info",
}

export enum LabradorEvent {
  LABRADOR_KEYVALUES = "labrador_keyvalues",
}

export enum LoadTimeEvent {
  SLOT_REQUESTED = "requested",
  SLOT_RENDERED = "rendered",
  AE_INIT = "init",
  /**
   * Used to track events from a third party vendor plugin or code, such as AudigentPlugin,
   * LiveConnect, or BrandSafety. These are emitted via the tracking code as a LOAD_TIMES event
   * type and should use the property on the paylaod {@link FandomTrackDataPayload.eventName} to
   * set the actual event name to be sent to the server.
   */
  VENDOR_TIME_UPDATE = "vendor_time_update",
}

export enum SizeInfoEvent {
  SLOT_SIZE_ESTABLISHED = "size_established",
}

/**
 * These events are sent to the Beacon endpoint __track/special/trackingevent
 *
 * This is a catch-all endpoint that contains many different types of events, not
 * only ones related to ads. For Odyssey, the primary event sent here is for noads.
 */
export enum TrackingEvent {
  TRACKING_EVENT = "trackingevent",
}

export enum TestEvent {
  TEST = "test",
}

export enum TrackingBoolean {
  YES = "yes",
  NO = "no",
  NOT_APPLICABLE = "", // temporarily empty string to match AE3
}

export type TrackEvent =
  | InfoEvent
  | BidderEvent
  | LabradorEvent
  | LoadTimeEvent
  | IdentityInfoEvent
  | SizeInfoEvent
  | TrackingEvent
  | TestEvent;

/** *****************************************************************************
 *
 * Info types
 *
 *******************************************************************************/

/**
 * Identifiers that come from GAM to designate which ad was selected to run in the slot
 */
export type CreativeGamIdentifiers = {
  advertiserId: googletag.ResponseInformation["advertiserId"] | undefined;
  lineItemId: googletag.ResponseInformation["lineItemId"] | undefined;
  creativeId: googletag.ResponseInformation["creativeId"] | undefined;
  orderId: googletag.ResponseInformation["campaignId"] | undefined;
};

export type SlotInfo = CreativeGamIdentifiers & {
  /**
   * This is the chosen size of the ad that is being rendered in the slot,
   * as reported by the SlotRenderEndedEvent.size.
   *
   * string: if there is a single size selected for this slot
   * null: empty ad slot
   * undefined: not applicable, such as for info events before the size has been selected
   */
  selectedSize: string | null | undefined;

  /**
   * This is an array of all the sizes available for this slot, as defined by
   * the node SlotConfig. Each size is parsed into the appropriate string
   * format, e.g. "300x600" for a googletag.SingleSizeArray or the string name
   * for a googletag.NamedSize e.g. "fluid".
   */
  configuredSizes: string[];

  /**
   * Array of requested sizes, as parsed from the contentUrl
   *
   * Question: is `contentUrl` the GAM request url? TODO: figure this out
   */
  parsedSizes: string[];
};

export type BidStatus =
  | "requested"
  | "on_time"
  | "declined"
  | "timedout"
  | "rejected"
  | "unknown"
  | "error";

export type BidResponseInfo = {
  bidderName: string;
  price?: string;
  size?: string;
  timeToRespond?: number;
  buyerId?: string;
  timedOut?: boolean;

  /**
   * Note: this will be required once we finish converting to new BidResponse format
   */
  status?: BidStatus;

  auctionId?: string;
  rejectionReason?: string;
  fullAuction?: boolean;
};

/** *****************************************************************************
 *
 * PAYLOADS
 *
 *******************************************************************************/
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface BaseTrackingPayload {
  // any fields that are used everywhere; none currently exist
}

export type BaseAdSlotPayload = BaseTrackingPayload & {
  slotName: string;
};

export interface AdInfoSlotPayload extends BaseTrackingPayload {
  slotName: string; // TODO move SlotName type to utils so we can type it correctly here
  rv?: number;
  slotInfo?: Pick<
    SlotInfo,
    | "creativeId"
    | "advertiserId"
    | "lineItemId"
    | "orderId"
    | "selectedSize"
    | "configuredSizes"
  >;
}

/**
 * A smaller version of the payload is sent for click events.
 *
 * NOTE: upon inspecting AE3 behavior and looking at these events, I believe that this was an oversight
 * and click events should actually also be sending the rest of the slot metadata. After refactor
 * this should be validated with downstream consumers, and if so this narrower type can go away.
 */
export type AdInfoClickPayload = Pick<AdInfoSlotPayload, "slotName" | "rv">;

export interface AdSizeSlotPayload extends BaseTrackingPayload {
  slotName: string;
  rv?: number;
  slotInfo: Pick<
    SlotInfo,
    | "advertiserId"
    | "creativeId"
    | "lineItemId"
    | "orderId"
    | "selectedSize"
    | "configuredSizes"
    | "parsedSizes"
  >;
}

/**
 * Extract the payload type from AdEngBiddersTrackData
 */
export type AdEngBiddersPayload = {
  slotName: string; // TODO move SlotName type to utils so we can type it correctly here
  baseSlotName: string; // TODO move BaseSlotName type to utils so we can type it correctly here
  rv?: number;
  bidInfo: BidResponseInfo;
  flags?: string[];
};

// TODO: continue defining specific payloads for each track type. This will be a separate PR

/** *****************************************************************************
 *
 * TrackData
 *
 * TrackData objects join a specific set of event types (Tracks) to the payloads they need
 *******************************************************************************/
export type LabradorTrackData = {
  event: LabradorEvent;
  payload: {
    flags: string[];
  };
};

export type AdInfoClickTrackData = {
  event: InfoEvent.SLOT_FORCE_CLOSE_INFO | InfoEvent.SLOT_CLICKED;
  payload: AdInfoClickPayload;
};

export type AdInfoTrackData = {
  event:
    | InfoEvent.SLOT_CODE_SERVED_INFO
    | InfoEvent.SLOT_RENDERED_INFO
    | InfoEvent.SLOT_REQUESTED_INFO
    | InfoEvent.SLOT_COLLAPSE_INFO;

  payload: AdInfoSlotPayload;
};

export type AdSizesTrackData = {
  event: SizeInfoEvent.SLOT_SIZE_ESTABLISHED;
  payload: AdSizeSlotPayload;
};
export type AdEngBiddersTrackData = {
  event: BidderEvent.BIDDER_RESPONSE;
  payload: AdEngBiddersPayload;
};

/**
 * TrackingTrackData is sent to the `__track/special/trackingevent` Beacon endpoint. This table
 * has a variety of events that are mostly non-ads use cases. The one ads-related use case is for logging when
 * there are no ads displayed on the page. This data is sent by the host app, but the data is inserted into
 * window.fandomTrack.q by UCP, because currently the adengine code is not loaded when ads are not displayed.
 *
 * UCP implementation: https://github.com/Wikia/unified-platform/blob/master/extensions/fandom/AdEngine/scripts/noAdsTracking.js
 */
export type TrackingTrackData = {
  event: TrackingEvent.TRACKING_EVENT;
  payload: {
    event_type: "no_ads";
    ga_label: string;
    ga_category: "no_ads";
  };
};

export type LoadTimeTrackData = {
  event: LoadTimeEvent;
  payload: {
    /**
     * Milliseconds since the start of the page load, for instance as obtained from `performance.now()`
     */
    time: DOMHighResTimeStamp;
    slotName?: string;
    eventName?: string;
  };
};

export type FandomPerformanceMarkTrackData = {
  event: PerformanceMarkName;
  payload: LoadTimeTrackData["payload"];
  alias?: string;
};

export type AdInfoTrackDataUnion = AdInfoTrackData | AdInfoClickTrackData;

export type IdentityInfoTrackData = {
  event: IdentityInfoEvent.IDENTITY_INFO;
  payload: {
    partnerName: string;
    partnerIdentityId: string;
  };
};

export type TestTrackData = {
  event: TestEvent.TEST;
  payload: object;
};

export type TrackDataUnion =
  | AdInfoTrackDataUnion
  | AdEngBiddersTrackData
  | IdentityInfoTrackData
  | LabradorTrackData
  | TrackingTrackData
  | LoadTimeTrackData
  | AdSizesTrackData
  | TestTrackData;

/** *****************************************************************************
 *
 * Tracking Window Object
 *
 * The window object created by the host application for handling tracking events in a unified manner.
 *******************************************************************************/
export type FandomTrack = {
  /**
   * The tracking queue. It holds the tracking data that is processed by the tracking module.
   * Events are consumed based on the type provided in the tracking data.
   * @see
   */
  q: TrackDataUnion[];
};
